import { JxtEventColorsEnum, fromISO, useGenerateRruleLabel } from '@jooxter/utils';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import JxtBookingAttendeesList from '../JxtBookingAttendeesList';
import JxtBookingLocation from '../JxtBookingLocation';
import JxtBookingOrganizer from '../JxtBookingOrganizer';
import JxtBookingRecurrence from '../JxtBookingRecurrence';
import JxtEventDate from '../JxtEventDate';
import JxtEventSummary from '../JxtEventSummary';
import { IJxtBookingTooltipCard } from './types';

/** What a booking shows when hovered on a planning: the content of its tooltip. */
const JxtBookingTooltipCard = ({
  booking,
  day,
  isBookingPending,
  locationName,
  floorNumber,
  nbAttendees = 0,
}: IJxtBookingTooltipCard) => {
  const { t } = useTranslation();
  const { rruleLabel } = useGenerateRruleLabel(
    fromISO(booking.start.dateTime, booking.resource.timezone),
    booking.rrule
  );

  return (
    <div className="bg-white border border-neutral-20 p-4 w-96 rounded-lg">
      <div className="flex flex-col gap-4">
        <div className="text-neutral-100 capitalize">
          {day &&
            DateTime.fromJSDate(day).toLocaleString({
              month: 'long',
              day: 'numeric',
              weekday: 'long',
            })}
        </div>
        <JxtEventSummary title={booking.summary} color={JxtEventColorsEnum.parse(booking.color)} />
        {booking.approved && <div className="text-neutral-100 text-xs">{t('this-booking-is-validated')}</div>}
        {isBookingPending && (
          <div className="text-neutral-100 text-xs">{t('this-booking-is-waiting-for-owner-approval')}</div>
        )}
        <JxtBookingLocation location={booking.resource.name} />
        {locationName && (
          <div className="flex items-center gap-2">
            <div className="h-6 w-5 flex items-center justify-center">
              <i className="fas fa-building fa-fw fa-lg text-neutral-60" />
            </div>
            <span className="text-body-m text-neutral-140 truncate">
              {locationName}
              {(!!floorNumber || floorNumber === 0) && ' - ' + t('resource-short-card-floor-number', { floorNumber })}
            </span>
          </div>
        )}
        <JxtEventDate
          range={{
            from: fromISO(booking.start.dateTime),
            to: fromISO(booking.end.dateTime),
          }}
        />
        {rruleLabel && <JxtBookingRecurrence recurrence={rruleLabel} />}
        <JxtBookingOrganizer name={`${booking.organizer.firstname} ${booking.organizer.lastname}`} />
        {nbAttendees > 0 && <JxtBookingAttendeesList physicalAttendees={nbAttendees} />}
      </div>
    </div>
  );
};

export default JxtBookingTooltipCard;
